/**
 * nh-wishlist.js — Nanou's House favourites (loaded on every page by snippets/nh-wishlist-header.liquid).
 *
 * Storage: localStorage "nh_wishlist" = list of product handles, newest first (max 100). No login needed.
 * - Hearts: every <button data-nh-wish="handle"> (snippets/nh-wishlist-heart.liquid) toggles the product.
 *   One delegated click listener; hearts added later (recommendations, infinite scroll, quick add)
 *   are synced by a MutationObserver.
 * - Header count: [data-nh-wish-count] (snippets/nh-wishlist-header.liquid).
 * - Toast "Ajouté à vos favoris · Voir mes favoris" after each click (one live region).
 * - Other tabs stay in sync (storage event).
 * - Statistics: each add / remove is sent to the ops Worker (<opsUrl>/wish-click).
 * - Signed-in customer (CFG.customer + CFG.sig): the list is also saved in her Shopify account
 *   (<opsUrl>/wishlist-sync → customer metafield nh_wish.list), so it follows her on every device.
 *   First time on a browser: guest favourites + account favourites are merged. After that, hearts
 *   added or removed on another device are applied here, and hearts changed here are kept.
 * Shared on window.NhWishlist: list(), has(h), add(h), remove(h), toggle(h), set(list), track(action, data).
 * Every change dispatches `nh:wishlist-changed` (detail = { list, handle, added }) on document.
 */
(() => {
  if (window.NhWishlist) return;

  const KEY = 'nh_wishlist';
  const MAX = 100;
  let CFG = {};
  try {
    CFG = JSON.parse(document.getElementById('nh-wishlist-config').textContent) || {};
  } catch (e) {}
  if (CFG.opsUrl) CFG.opsUrl = String(CFG.opsUrl).trim().replace(/\/+$/, '');

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) {
      return [];
    }
  };
  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (e) {}
  };

  function emit(list, handle, added) {
    sync(list);
    pushAccount(list);
    document.dispatchEvent(new CustomEvent('nh:wishlist-changed', { detail: { list, handle, added } }));
  }

  const api = {
    list: read,
    has: (h) => read().includes(h),
    set(list) {
      const clean = [...new Set(list.filter(Boolean))].slice(0, MAX);
      write(clean);
      emit(clean, null, null);
    },
    add(h) {
      const list = [h, ...read().filter((x) => x !== h)];
      write(list);
      emit(list, h, true);
    },
    remove(h) {
      const list = read().filter((x) => x !== h);
      write(list);
      emit(list, h, false);
    },
    toggle(h) {
      const added = !api.has(h);
      added ? api.add(h) : api.remove(h);
      return added;
    },
    track,
  };
  window.NhWishlist = api;

  /* ---------- hearts and header count ---------- */

  function paint(button, list) {
    const on = list.includes(button.dataset.nhWish);
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    const label = on ? button.dataset.labelRemove : button.dataset.labelAdd;
    const text = button.querySelector('.nh-wish__text');
    if (text) text.textContent = label;
    else if (button.dataset.nhWishTitle) button.setAttribute('aria-label', `${label} : ${button.dataset.nhWishTitle}`);
  }

  function sync(list = read()) {
    document.querySelectorAll('[data-nh-wish]').forEach((b) => paint(b, list));
    document.querySelectorAll('[data-nh-wish-count]').forEach((el) => {
      el.textContent = list.length > 99 ? '99+' : String(list.length);
      el.hidden = list.length === 0;
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-nh-wish]');
      if (!button) return;
      // The heart sits inside product cards: never open the product.
      event.preventDefault();
      event.stopPropagation();
      const handle = button.dataset.nhWish;
      const added = api.toggle(handle);
      if (added) {
        button.classList.remove('is-popping');
        void button.offsetWidth; // restart the animation
        button.classList.add('is-popping');
        document.querySelectorAll('.nh-wish-header').forEach((h) => {
          h.classList.remove('is-bumping');
          void h.offsetWidth;
          h.classList.add('is-bumping');
        });
      }
      toast(added);
      track(added ? 'add' : 'remove', { handle, title: button.dataset.nhWishTitle || '' });
    },
    true
  );

  // Hearts rendered later (recommendations, quick add, infinite scroll, section re-renders).
  let pending = false;
  new MutationObserver((records) => {
    if (pending) return;
    const hasHeart = records.some((r) =>
      [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches('[data-nh-wish], [data-nh-wish-count]') || n.querySelector('[data-nh-wish], [data-nh-wish-count]')))
    );
    if (!hasHeart) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      sync();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('storage', (e) => {
    if (e.key === KEY) emit(read(), null, null);
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) sync();
  });

  /* ---------- toast ---------- */

  let toastEl = null;
  let toastTimer = 0;
  function toast(added) {
    const text = added ? CFG.added : CFG.removed;
    if (!text) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'nh-wish-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.append(toastEl);
    }
    toastEl.textContent = '';
    const span = document.createElement('span');
    span.textContent = text;
    toastEl.append(span);
    const onPage = CFG.pageUrl && window.location.pathname.replace(/\/$/, '') === CFG.pageUrl.replace(/\/$/, '');
    if (added && CFG.pageUrl && CFG.link && !onPage) {
      const a = document.createElement('a');
      a.href = CFG.pageUrl;
      a.textContent = CFG.link;
      toastEl.append(a);
    }
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
  }

  /* ---------- signed-in customer: account copy ---------- */

  // Three lists: L = this browser, A = the account (rendered by Liquid at page load),
  // S = the last list this browser knows the account had (snapshot, saved after each successful save).
  // Changes made elsewhere (A vs S) are applied to L; changes made here are kept. A save that has not
  // reached the account yet (page left too fast, network) never wipes anything: A is then still S.
  const ACCOUNT = !!(CFG.customer && CFG.sig && CFG.opsUrl);
  const SNAP = `nh_wishlist_account_${CFG.customer}`;
  let pushTimer = 0;
  let lastPushed = '';
  const readSnap = () => {
    try {
      const v = JSON.parse(localStorage.getItem(SNAP));
      return Array.isArray(v) ? v : null;
    } catch (e) {
      return null;
    }
  };
  const writeSnap = (list) => {
    try {
      localStorage.setItem(SNAP, JSON.stringify(list));
    } catch (e) {}
  };

  function pushAccount(list) {
    if (!ACCOUNT) return;
    const value = JSON.stringify(list.slice(0, MAX));
    if (value === lastPushed) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(() => {
      lastPushed = value;
      const body = JSON.stringify({ customer: CFG.customer, sig: CFG.sig, handles: JSON.parse(value) });
      fetch(`${CFG.opsUrl}/wishlist-sync`, { method: 'POST', body, keepalive: true })
        .then((r) => {
          if (r.ok) writeSnap(JSON.parse(value));
          else lastPushed = ''; // try again on the next change or page
        })
        .catch(() => {
          lastPushed = '';
        });
    }, 600);
  }

  function syncAccountOnLoad() {
    if (!ACCOUNT) return;
    const A = (CFG.account || []).filter((h) => typeof h === 'string' && h);
    const L = read();
    const S = readSnap();
    let next;
    if (!S) {
      // First time signed in on this browser: keep the guest favourites and the account ones.
      next = [...L, ...A.filter((h) => !L.includes(h))];
    } else {
      const addedElsewhere = A.filter((h) => !S.includes(h) && !L.includes(h));
      const removedElsewhere = S.filter((h) => !A.includes(h));
      next = [...addedElsewhere, ...L.filter((h) => !removedElsewhere.includes(h))];
    }
    if (JSON.stringify(next) !== JSON.stringify(L)) write(next);
    lastPushed = JSON.stringify(A); // what the account holds now
    if (JSON.stringify(next) === lastPushed) writeSnap(A);
    else pushAccount(next);
  }

  /* ---------- statistics ---------- */

  const device = () => (/Mobi|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : /iPad|Tablet/i.test(navigator.userAgent) ? 'tablet' : 'desktop');

  function track(action, data = {}) {
    if (!CFG.opsUrl) return;
    const trackingUrl = `${CFG.opsUrl}/wish-click`;
    try {
      const body = JSON.stringify(
        Object.assign({}, data, {
          action,
          count: read().length,
          device: device(),
          path: window.location.pathname,
          locale: CFG.locale,
          shop: CFG.shop,
        })
      );
      if (!(navigator.sendBeacon && navigator.sendBeacon(trackingUrl, body))) {
        fetch(trackingUrl, { method: 'POST', body, keepalive: true, mode: 'no-cors' }).catch(() => {});
      }
    } catch (e) {}
  }

  syncAccountOnLoad();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => sync(), { once: true });
  else sync();
})();
